import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Data, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

export interface Breadcrumb {
  path: string;
  label?: string;
  icon?: string;
  children?: Breadcrumb[];
  dynamic?: boolean;
}

@Injectable()
export class BreadcrumbsService {
  #route = inject(ActivatedRoute);
  #router = inject(Router);
  #destroyRef = inject(DestroyRef);
  breadcrumbs = signal<Breadcrumb[]>([]);

  constructor() {
    this.#onRouteChange();

    this.#router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() => this.#onRouteChange());
  }

  #onRouteChange() {
    let currentRoute = this.#route.root;
    const data: Breadcrumb[] = [];

    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
      let breadcrumb: Breadcrumb | undefined = undefined;

      const routeConfig = currentRoute.routeConfig;
      const routeData: Data | undefined = routeConfig?.data;
      const hasDynamicData =
        currentRoute.snapshot?.data['breadcrumb']?.['dynamic'];
      if (routeData && routeData['breadcrumb']) {
        breadcrumb = routeData['breadcrumb'];
      } else if (hasDynamicData) {
        breadcrumb = currentRoute.snapshot.data['breadcrumb'];
      }
      if (
        breadcrumb &&
        routeConfig &&
        (routeConfig['path'] || routeConfig['path'] === '')
      ) {
        data.push({
          ...breadcrumb,
          path: routeConfig.path,
        });
      }
    }

    this.breadcrumbs.set(this.#formatBreadcrumbsPaths(data));
  }

  #formatBreadcrumbsPaths(breadcrumbs: Breadcrumb[]) {
    return breadcrumbs.map<Breadcrumb>((value, index, array) => {
      const actualPath = array
        .map(val => val.path)
        .slice(0, index + 1)
        .join('/');

      return {
        ...value,
        path: actualPath,
      };
    });
  }

  updateBreadcrumbData(path: string, data: Omit<Breadcrumb, 'path'>) {
    const breadcrumbs = this.breadcrumbs();
    const index = breadcrumbs.findIndex(breadcrumb => breadcrumb.path === path);

    if (index === -1) {
      throw Error(`Breadcrumb with path '${path}' don't exist`);
    }

    breadcrumbs[index] = {
      path: breadcrumbs[index].path,
      ...data,
    };

    this.breadcrumbs.set([...breadcrumbs]);
  }
}
